// Ubicación en el repo: src/pages/Catalog.jsx
// Catálogo e Inventario — solo Admin (mismo guard de UX que
// Branches.jsx/Professionals.jsx; RLS `catalog_insert`/`catalog_update` es
// la protección real). Prerrequisito del módulo Audífonos. Mismo
// contenido que la sub-pestaña "Catálogo e Inventario" del prototipo
// (línea ~2757): filtro por tipo, alerta de stock bajo, agregar/editar/
// ajustar stock/eliminar.
//
// Fase A del módulo de Inventario/Contabilidad (24-ago-2026, ver
// plan-inventario-y-contabilidad.md): se agregan 3 sub-pestañas internas
// (Categorías/Marcas/Proveedores, migración 0046) — mismo criterio que
// Settings.jsx agrupa pestañas de nivel superior, acá se agrupan las de
// este módulo para no llenar la barra de Configuración de más ítems.
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { listCatalog, softDeleteCatalogItem } from '../lib/catalog';
import { listActiveCategories } from '../lib/categories';
import { formatCurrency } from '../lib/format';
import CatalogFormModal from '../components/CatalogFormModal';
import AdjustStockModal from '../components/AdjustStockModal';
import TransferStockModal from '../components/TransferStockModal';
import CategoriesList from '../components/CategoriesList';
import BrandsList from '../components/BrandsList';
import SuppliersList from '../components/SuppliersList';
import CatalogTrash from '../components/CatalogTrash';
import Spinner from '../components/Spinner';

const INNER_TABS = [
  { key: 'productos', label: 'Productos' },
  { key: 'categorias', label: 'Categorías' },
  { key: 'marcas', label: 'Marcas' },
  { key: 'proveedores', label: 'Proveedores' },
  { key: 'papelera', label: '🗑️ Papelera' },
];

export default function Catalog() {
  const { professional, activeBranchId, selectableBranches } = useOutletContext();
  const activeBranch = selectableBranches.find((b) => b.id === activeBranchId) || null;

  const [innerTab, setInnerTab] = useState('productos');
  // Filtro rápido por categoría (Fase A, 24-ago-2026) — antes era una
  // lista fija de 5 botones (Audífono/Pilas/Molde/Funda/Otro, el viejo
  // enum `type`); reemplazado por las categorías reales para que una
  // categoría nueva (ej. "Monturas") aparezca sola acá, sin tocar código.
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [stockTarget, setStockTarget] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);

  useEffect(() => {
    listActiveCategories().then(setCategories).catch(() => setCategories([]));
  }, [innerTab]); // se refresca al volver a "Productos", por si se creó/editó una categoría en la otra pestaña

  // Fase B (migración 0049, 25-ago-2026): el stock mostrado es el de la
  // sucursal activa, no un número global — por eso `refresh` también
  // depende de `activeBranchId` (cambiar de sucursal arriba en la barra
  // recarga la tabla con el stock de la nueva sucursal).
  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      setItems(await listCatalog({ categoryId: filterCategoryId || undefined, branchId: activeBranchId }));
    } catch (err) {
      setError(err.message || 'No se pudo cargar el catálogo.');
    } finally {
      setLoading(false);
    }
  }, [filterCategoryId, activeBranchId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  function handleEdit(item) {
    setEditTarget(item);
    setFormOpen(true);
  }

  async function handleDelete(item) {
    const ok = window.confirm(`¿Eliminar "${item.brand} ${item.model}" del catálogo?`);
    if (!ok) return;
    try {
      await softDeleteCatalogItem(item.id, professional.id);
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el producto.');
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  if (!professional.is_admin) {
    return (
      <div className="patients-page">
        <h1>Catálogo</h1>
        <p className="text-muted">Solo un Admin puede gestionar el catálogo.</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {INNER_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={innerTab === t.key ? 'button-primary' : 'button-secondary'}
            onClick={() => setInnerTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {innerTab === 'categorias' && <CategoriesList actorId={professional.id} />}
      {innerTab === 'marcas' && <BrandsList actorId={professional.id} />}
      {innerTab === 'proveedores' && <SuppliersList actorId={professional.id} />}
      {innerTab === 'papelera' && <CatalogTrash />}

      {innerTab === 'productos' && (
        <>
          <div className="patients-page__toolbar">
            <h1>Catálogo e inventario</h1>
            <div className="patients-page__toolbar-right">
              <button type="button" className="button-primary" onClick={handleNew}>
                + Agregar al catálogo
              </button>
            </div>
          </div>

          <p className="field-hint">
            Registra audífonos y también otros insumos (pilas, moldes, fundas, monturas, lentes, etc.). Al
            vender un audífono desde Audífonos, el stock se descuenta solo. El stock que ves abajo es el de{' '}
            <strong>{activeBranch?.name || 'la sucursal activa'}</strong> — cambia de sucursal arriba para ver
            o ajustar el stock de otra, o usa "Traspasar" para mover mercadería entre ellas.
          </p>

          {error && <p className="auth-error">{error}</p>}

          <div className="checkbox-group" style={{ marginBottom: 12 }}>
            <button
              type="button"
              className={filterCategoryId === '' ? 'button-primary' : 'button-secondary'}
              onClick={() => setFilterCategoryId('')}
            >
              Todos
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={filterCategoryId === c.id ? 'button-primary' : 'button-secondary'}
                onClick={() => setFilterCategoryId(c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>
          {categories.length === 0 && (
            <p className="field-hint" style={{ marginTop: -8, marginBottom: 12 }}>
              Todavía no hay categorías creadas — ve a la pestaña "Categorías" para crear la primera (ej.
              "Audífono", "Monturas", "Lentes ópticos").
            </p>
          )}

          <table className="patients-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Precio venta</th>
                <th>Costo</th>
                <th>Stock · {activeBranch?.name || 'sucursal activa'}</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}><Spinner inline /></td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6}>No hay productos registrados.</td>
                </tr>
              ) : (
                items.map((item) => {
                  const lowStock = Number(item.stock) <= Number(item.stock_min);
                  const isPack = item.power_type === 'Recargable';
                  return (
                    <tr key={item.id}>
                      <td>
                        {item.brand_ref?.name || item.brand} {item.model}
                        {item.sku && <span className="text-muted"> · {item.sku}</span>}
                        {isPack && (
                          <span className="badge badge-neutral" style={{ marginLeft: 6 }}>
                            Recargable · Pack x2
                          </span>
                        )}
                      </td>
                      <td>{item.category?.name || item.type || '—'}</td>
                      <td>
                        {formatCurrency(item.price)}
                        {isPack && <span className="text-muted"> /pack</span>}
                      </td>
                      <td>{item.cost_price != null ? formatCurrency(item.cost_price) : '—'}</td>
                      <td>
                        {item.stock}
                        {lowStock && (
                          <span className="badge badge-warn" style={{ marginLeft: 6 }}>
                            Stock bajo
                          </span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button type="button" className="link-button" onClick={() => setStockTarget(item)}>
                          📦 Ajustar stock
                        </button>
                        <button type="button" className="link-button" onClick={() => setTransferTarget(item)}>
                          🔁 Traspasar
                        </button>
                        <button type="button" className="link-button" onClick={() => handleEdit(item)}>
                          Editar
                        </button>
                        <button type="button" className="link-button link-button--danger" onClick={() => handleDelete(item)}>
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </>
      )}

      {formOpen && (
        <CatalogFormModal
          item={editTarget}
          branchId={activeBranchId}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}

      {stockTarget && (
        <AdjustStockModal
          item={stockTarget}
          branchId={activeBranchId}
          branchName={activeBranch?.name}
          onClose={() => setStockTarget(null)}
          onSaved={() => {
            setStockTarget(null);
            refresh();
          }}
        />
      )}

      {transferTarget && (
        <TransferStockModal
          item={transferTarget}
          fromBranchId={activeBranchId}
          branches={selectableBranches}
          onClose={() => setTransferTarget(null)}
          onSaved={() => {
            setTransferTarget(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
